import L from 'leaflet';

function resolveContainer(elOrId) {
    if (typeof elOrId === 'string') return document.getElementById(elOrId);
    return elOrId;
}

async function waitForContainerSize(el, { tries = 25, delayMs = 50 } = {}) {
    for (let i = 0; i < tries; i++) {
        if (el && el.clientWidth > 0 && el.clientHeight > 0) return true;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, delayMs));
    }
    return !!el;
}

export function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject;
        img.src = url;
    });
}

export async function createFlatMap(elId, imageUrl, opts = {}) {
    const { width: w, height: h } = await loadImage(imageUrl);
    const bounds = L.latLngBounds([[0, 0], [h, w]]);

    const container = resolveContainer(elId);
    if (!container) {
        throw new Error(`Leaflet container not found: ${String(elId)}`);
    }

    // Leaflet can render blank if initialized on a 0x0 element (common during route/layout transitions).
    await waitForContainerSize(container);

    const map = L.map(container, {
        crs: L.CRS.Simple,
        zoomControl: false,
        minZoom: -2,
        maxZoom: 4,
        zoomSnap: 0.5,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        ...opts,
    });
    L.imageOverlay(imageUrl, bounds, { interactive: false }).addTo(map);

    map.fitBounds(bounds, { animate: false });

    // Force recalculation once DOM settles; fixes intermittent blank maps.
    setTimeout(() => {
        try { map.invalidateSize(true); } catch (e) { /* noop */ }
    }, 0);

    return { map, bounds, size: { w, h } };
}

export function addGrid(map, bounds, cols = 20, rows = 17, opts = {}) {
    // По ТЗ (встреча 27.08.2026, п.5): координатная сетка приложения должна
    // совпадать с линиями оригинальной карты 1942 г. Так как разные подложки
    // могут иметь разный смещённый origin, поддерживаем настройку:
    //   offsetX / offsetY — сдвиг всей сетки в единицах карты (доли ширины/высоты)
    //   scaleX  / scaleY  — множитель размера клетки (например 1.02 = растянуть на 2%)
    //   layer             — куда добавлять (LayerGroup, чтоб потом целиком удалить)
    const {
        offsetX = 0,
        offsetY = 0,
        scaleX = 1,
        scaleY = 1,
        layer = null,
    } = opts;

    const south = bounds.getSouth();
    const west = bounds.getWest();
    const north = bounds.getNorth();
    const east = bounds.getEast();

    const width = east - west;
    const height = north - south;

    const colW = (width / cols) * scaleX;
    const rowH = (height / rows) * scaleY;

    // Абсолютный сдвиг: пользовательский offset задаётся в долях
    // от ширины/высоты подложки, чтоб не зависел от масштаба.
    const dx = offsetX * width;
    const dy = offsetY * height;

    const labelOffsetY = height * 0.015;
    const labelOffsetX = width * 0.01;

    const LETTERS = ['А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ж', 'З', 'И', 'К', 'Л', 'М', 'Н', 'О', 'П', 'Р', 'С', 'Т', 'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Ъ', 'Ы', 'Ь', 'Э', 'Ю', 'Я'];

    const lineOpts = { color: '#888', weight: 1, opacity: 0.5, interactive: false };
    const add = (l) => (layer ? l.addTo(layer) : l.addTo(map));

    for (let i = 0; i <= cols; i++) {
        const x = west + dx + i * colW;
        add(L.polyline([[south, x], [north, x]], lineOpts));

        if (i < cols) {
            const cx = x + colW / 2;
            const letter = LETTERS[i] || '';

            const topLat = north - labelOffsetY;
            const bottomLat = south + labelOffsetY;

            const icon = L.divIcon({
                className: 'grid-label',
                html: `<span>${letter}</span>`,
                iconSize: [20, 20],
            });

            add(L.marker([topLat, cx], { icon, interactive: false, keyboard: false }));
            add(L.marker([bottomLat, cx], { icon, interactive: false, keyboard: false }));
        }
    }

    for (let j = 0; j <= rows; j++) {
        const y = south + dy + j * rowH;
        add(L.polyline([[y, west], [y, east]], lineOpts));

        if (j < rows) {
            const cy = y + rowH / 2;
            // Нумерация — 1 сверху вниз (ТЗ встречи 27.08.2026, п.11)
            const label = (rows - j).toString();

            const leftLng = west + labelOffsetX;
            const rightLng = east - labelOffsetX;

            const icon = L.divIcon({
                className: 'grid-label',
                html: `<span>${label}</span>`,
                iconSize: [20, 20],
            });

            add(L.marker([cy, leftLng], { icon, interactive: false, keyboard: false }));
            add(L.marker([cy, rightLng], { icon, interactive: false, keyboard: false }));
        }
    }
}

export function addViewportGrid(map, cols = 20, rows = 17, group) {
    const size = map.getSize();
    const colW = size.x / cols;
    const rowH = size.y / rows;
    const LETTERS = ['А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ж', 'З', 'И', 'К', 'Л', 'М', 'Н', 'О', 'П', 'Р', 'С', 'Т', 'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Ъ', 'Ы', 'Ь', 'Э', 'Ю', 'Я'];

    const add = (layer) => (group ? layer.addTo(group) : layer.addTo(map));

    // Вертикальные линии + буквы сверху
    for (let i = 0; i <= cols; i++) {
        const x = i * colW; // пиксели по горизонтали
        const top = map.containerPointToLatLng([x, 0]);
        const bottom = map.containerPointToLatLng([x, size.y]);
        add(L.polyline([top, bottom], { color: '#888', weight: 1, opacity: 0.5, interactive: false }));
        if (i < cols) {
            const cx = x + colW / 2;
            const labelLatLng = map.containerPointToLatLng([cx, 6]); // немного ниже верхнего края
            add(L.marker(labelLatLng, { icon: L.divIcon({ className: 'grid-label', html: `<span>${LETTERS[i] || ''}</span>`, iconSize: [20, 20] }), interactive: false, keyboard: false }));
        }
    }
    // Горизонтальные линии + цифры слева. Нумерация: 1 сверху → rows снизу.
    // (в viewport координата y растёт сверху вниз, поэтому j+1 уже правильно.)
    for (let j = 0; j <= rows; j++) {
        const y = j * rowH; // пиксели по вертикали
        const left = map.containerPointToLatLng([0, y]);
        const right = map.containerPointToLatLng([size.x, y]);
        add(L.polyline([left, right], { color: '#888', weight: 1, opacity: 0.5, interactive: false }));
        if (j < rows) {
            const cy = y + rowH / 2;
            const labelLatLng = map.containerPointToLatLng([8, cy]); // чуть правее левого края
            add(L.marker(labelLatLng, { icon: L.divIcon({ className: 'grid-label', html: `<span>${j + 1}</span>`, iconSize: [20, 20] }), interactive: false, keyboard: false }));
        }
    }
}
